import { Button, Select, Spinner, TextField } from '@cimpress/react-components';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { CardBuilder, Control, Page, PreviewRow, PreviewRows, PreviewSection, type Card } from '../../../Components';
import { DragDropImageUploader } from '../../../Components/common/dragDropImageUploader/DragDropImageUploader';
import { useEditImageParams, MODEL_OPTIONS } from '../../../Components/EditImage/v3/useEditImageParams';
import type { SelectorOption } from '../../../Components/common';
import { IMAGE_COUNT_OPTIONS } from '../../../common/constants';
import { postEditImage, type EditImageInput } from '../../../api/editImageApi';
import { useCancelableEffect } from '../../../tools';

const LOADING_CONTAINER_STYLE: CSSProperties = {
    textAlign: 'center' as const,
    padding: '20px',
};

const LOADING_TEXT_STYLE: CSSProperties = {
    marginTop: '10px',
};

const HELPER_TEXT_STYLE: CSSProperties = {
    color: '#666',
    fontSize: '12px',
    marginTop: '4px',
};

const EDITED_IMAGE_CARD_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
};

const EDITED_IMAGE_STYLE: CSSProperties = {
    maxWidth: '100%',
    height: 'auto',
    cursor: 'pointer',
};

const createEditedImageCard = (resultImageUrl: string, onEditAgain: (url: string) => void, index: number) => {
    return CardBuilder.ofType('childComponent')
        .withTitle(`Edited Image ${index + 1}`)
        .withChildComponent(() => (
            <div style={EDITED_IMAGE_CARD_STYLE}>
                <img
                    src={resultImageUrl}
                    alt={`Edited Image ${index + 1}`}
                    style={EDITED_IMAGE_STYLE}
                    onClick={() => window.open(resultImageUrl, '_blank')}
                />
                <Button onClick={() => onEditAgain(resultImageUrl)}>Use as Image URL</Button>
            </div>
        ))
        .build();
};

const EditImagePage = () => {
    const {
        prompt,
        description,
        imageUrl,
        imageCount,
        modelSelectedOption,
        setPrompt,
        setDescription,
        setImageUrl,
        setImageCount,
        setModelSelectedOption,
    } = useEditImageParams();
    const [resultRow, setResultRow] = useState(PreviewRow());
    const [isLoading, setIsLoading] = useState(false);
    const [callGenerate, setCallGenerate] = useState(false);
    const [originalImageRow, setOriginalImageRow] = useState(PreviewRow());
    const [isReiterating, setIsReiterating] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);
    const isReiteratingRef = useRef(false);
    const imageUrlRef = useRef(imageUrl);

    // Update imageUrlRef when imageUrl changes
    useEffect(() => {
        imageUrlRef.current = imageUrl;
    }, [imageUrl]);

    // Create original image row when imageUrl changes
    useEffect(() => {
        if (imageUrl && imageUrl.trim() !== '') {
            const originalCard = CardBuilder.ofType('imageUrl')
                .withTitle('Original Image')
                .withData({ imageUrl: imageUrl })
                .build();
            setOriginalImageRow(PreviewRow(originalCard));
        } else {
            setOriginalImageRow(PreviewRow());
            setResultRow(PreviewRow());
        }
    }, [imageUrl]);

    // Clear edited images when imageUrl changes (but not when using "Use as Image URL")
    useEffect(() => {
        // Only clear if we're not in the middle of using an edited image
        if (imageUrl && imageUrl.trim() !== '' && !isReiteratingRef.current) {
            setResultRow(PreviewRow());
        }
    }, [imageUrl]);

    useCancelableEffect(
        (signal) => {
            const onEditAgain = (generatedImageUrl: string) => {
                // Don't clear resultRow - keep all edited images visible
                isReiteratingRef.current = true;
                setImageUrl(generatedImageUrl);
                setIsReiterating(true);
            };

            (async () => {
                if (callGenerate) {
                    setIsLoading(true);

                    try {
                        const imageResultCards: Card[] = [];
                        const resultImagePromises: Promise<string>[] = [];

                        for (let i = 0; i < imageCount; i++) {
                            const params: EditImageInput = {
                                prompt,
                                imageUrl: imageUrlRef.current,
                                signal,
                                ...(description && { description }),
                                ...(modelSelectedOption.value && { model: modelSelectedOption.value }),
                            };

                            const resultImagePromise = postEditImage(params);
                            resultImagePromises.push(resultImagePromise);
                        }

                        // Wait for all promises to settle
                        const editImageResults = await Promise.allSettled(resultImagePromises);

                        // Build the cards using resolved image URLs
                        for (let i = 0; i < editImageResults.length; i++) {
                            const editImageResult = editImageResults[i];
                            if (editImageResult.status === 'fulfilled') {
                                const imageResultCard = createEditedImageCard(editImageResult.value, onEditAgain, i);
                                imageResultCards.push(imageResultCard);
                            } else {
                                const errorCard = CardBuilder.ofType('documentUrl')
                                    .withTitle(`Error ${i + 1}`)
                                    .withLoader(async () => {
                                        throw editImageResult.reason;
                                    })
                                    .build();
                                imageResultCards.push(errorCard);
                            }
                        }

                        setResultRow(PreviewRow(...imageResultCards));
                    } catch (error) {
                        const errorCard = CardBuilder.ofType('documentUrl')
                            .withTitle('Error')
                            .withLoader(async () => {
                                throw error;
                            })
                            .build();

                        setResultRow(PreviewRow(errorCard));
                    }

                    setIsLoading(false);
                    setCallGenerate(false);
                    setIsReiterating(false);
                    // Reset the ref after a short delay to ensure it's available for the next operation
                    setTimeout(() => {
                        isReiteratingRef.current = false;
                    }, 100);
                }
            })();
        },
        [callGenerate, prompt, description, imageCount, modelSelectedOption.value, setImageUrl],
    );

    const onModelChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setModelSelectedOption(selected);
    };

    const onImageCountChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setImageCount(parseInt(selected.value));
    };

    function onClickGenerate() {
        setCallGenerate(true);
    }

    return (
        <Page>
            <Page.Title>Edit Image</Page.Title>

            <Page.Controls>
                <Control spacing="standard">
                    <Control.Title>Image URL</Control.Title>
                    <TextField
                        label="Image URL"
                        onChange={(e) => setImageUrl(e.target.value)}
                        value={imageUrl}
                        required
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Or Upload Image</Control.Title>
                    <DragDropImageUploader
                        onUploadingStateChange={setIsImageUploading}
                        onChange={setImageUrl}
                        onClear={() => setImageUrl('')}
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Prompt</Control.Title>
                    <TextField label="Prompt" onChange={(e) => setPrompt(e.target.value)} value={prompt} required />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Image description</Control.Title>
                    <TextField
                        label="Image description (Optional)"
                        onChange={(e) => setDescription(e.target.value)}
                        value={description}
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Model</Control.Title>
                    <Select
                        onChange={onModelChange}
                        value={modelSelectedOption}
                        options={MODEL_OPTIONS}
                        label="Model"
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Image Count</Control.Title>
                    <Select
                        onChange={onImageCountChange}
                        value={IMAGE_COUNT_OPTIONS.find((opt) => opt.value === imageCount.toString())}
                        options={IMAGE_COUNT_OPTIONS}
                        label="Image Count"
                    />
                </Control>

                <Control spacing="standard">
                    <Button
                        onClick={onClickGenerate}
                        disabled={!imageUrl || !prompt || callGenerate || isImageUploading}
                    >
                        Edit Image
                    </Button>
                    {isImageUploading && <p style={HELPER_TEXT_STYLE}>Uploading image...</p>}
                </Control>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction={
                        'This is a page designed to edit images. Fill in the Image URL, drag and drop an image or just upload it from your computer. Write a Prompt (describe the changes you want to make to your image), then click Edit Image. To re-edit a generated image, click "Use as Input" on any result, modify the prompt as needed, then click "Edit Image".'
                    }
                    showIntro={!resultRow.length && !originalImageRow.length}
                    rows={PreviewRows(
                        originalImageRow,
                        ...(isLoading
                            ? [
                                  PreviewRow(
                                      CardBuilder.ofType('childComponent')
                                          .withTitle('Processing...')
                                          .withChildComponent(() => (
                                              <div style={LOADING_CONTAINER_STYLE}>
                                                  <Spinner />
                                                  <p style={LOADING_TEXT_STYLE}>
                                                      {isReiterating ? 'Re-editing image...' : 'Editing image...'}
                                                  </p>
                                              </div>
                                          ))
                                          .build(),
                                  ),
                              ]
                            : []),
                        // Only show resultRow if we're not re-iterating (to hide previous edited images during re-editing)
                        ...(isReiterating && isLoading ? [] : [resultRow]),
                    )}
                />
            </Page.Content>
        </Page>
    );
};

export default EditImagePage;
